import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { safeHtml } from '@/lib/sanitize';
import { 
  ArrowLeft, 
  Bot, 
  Database, 
  MessageSquare, 
  Settings, 
  Calendar,
  FileText,
  Code,
  BarChart3,
  Shield,
  Users,
  Zap,
  Home,
  CheckCircle,
  PlayCircle,
  ArrowRight,
  Lightbulb,
  ExternalLink,
  Upload,
  Globe,
  MousePointer,
  Palette,
  Gift,
  DollarSign,
  TrendingUp,
  Award,
  Share2
} from 'lucide-react';

// Visual placeholder component for screenshots - moved outside to avoid recreation on each render
const ScreenshotPlaceholder = ({ icon: Icon, title, description, color = 'blue' }) => {
  const colorClasses = {
    blue: 'from-blue-500 to-indigo-600',
    purple: 'from-purple-500 to-pink-600',
    emerald: 'from-emerald-500 to-teal-600',
    amber: 'from-amber-500 to-orange-600',
    slate: 'from-slate-600 to-slate-800'
  };
  
  return (
    <div className={`bg-gradient-to-br ${colorClasses[color]} p-8 flex flex-col items-center justify-center text-white h-full min-h-[200px]`}>
      <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center mb-4">
        <Icon className="w-8 h-8" />
      </div>
      <h4 className="font-semibold text-lg mb-1">{title}</h4>
      <p className="text-white/80 text-sm text-center">{description}</p>
    </div>
  );
};

export const UserManualPage = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    // Navigate to dashboard instead of using history.back which can cause logout
    navigate('/dashboard');
  };

  const sections = [
    {
      id: 'overview',
      title: 'Platform Overview',
      icon: Zap,
      visual: { icon: Bot, title: 'AI Employees', description: 'Enterprise-grade conversational AI', color: 'blue' },
      content: `Harmonix is a comprehensive SaaS platform for creating AI-powered agents with custom knowledge bases. 
      Businesses can build intelligent AI employees that answer questions using uploaded documents and connected APIs, with source citations.`,
      highlights: ['AI-powered conversations', 'Custom knowledge bases', 'Multi-industry support']
    },
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: Bot,
      visual: { icon: PlayCircle, title: 'Create AI Employee', description: '6-Step Wizard: Info → Knowledge → Personality → Channels → Appearance → Deploy', color: 'emerald' },
      content: `
        <h4>Creating Your First AI Employee (6-Step Wizard)</h4>
        <p>Navigate to <strong>AI Employees → New AI Employee</strong>. The wizard walks you through 6 steps:</p>
        <ol>
          <li><strong>Basic Info</strong> — Name, description, welcome message, and template (Sales, Support, Legal, etc.)</li>
          <li><strong>Knowledge Base</strong> — Upload files (PDF, Word, text), add URLs, or paste text directly</li>
          <li><strong>Personality</strong> — Set the system prompt, response tone, and behavior rules</li>
          <li><strong>Social Channels</strong> — Connect WhatsApp, Facebook Messenger, Instagram, or Telegram. The website widget is always available automatically.</li>
          <li><strong>Appearance</strong> — Choose widget style (Floating Button or Side Tab), position, colors, icon, and button label</li>
          <li><strong>Deploy</strong> — Review your setup and launch. Get your embed code instantly.</li>
        </ol>
        <p class="mt-4">Your AI agent is live immediately after creation and starts responding to messages on all connected channels.</p>
      `,
      highlights: ['6-step guided wizard', 'Connect social channels during setup', 'Live immediately after creation']
    },
    {
      id: 'knowledge-base',
      title: 'Knowledge Base Management',
      icon: Database,
      visual: { icon: Upload, title: 'Upload Knowledge', description: 'Documents, URLs, Sitemaps, APIs', color: 'purple' },
      content: `
        <h4>Adding Knowledge to Your AI Employee</h4>
        <p>There are three ways to add knowledge. All content is chunked, embedded, and retrieved with source citations at chat time.</p>

        <h5 class="mt-4">1. File Upload</h5>
        <p>Upload one or more files in a single drag-and-drop. Supported formats: <strong>.pdf, .docx, .txt, .csv, .md</strong>. Maximum <strong>10 MB per file</strong>. Unsupported types (.xlsx, images, .zip, etc.) are blocked at the upload dialog with a clear error message so you never wait through a broken upload.</p>

        <h5 class="mt-4">2. URL &amp; Sitemap Import</h5>
        <p>Scrape any public webpage or bulk-import from a sitemap. Click <strong>Add URL</strong> and paste the address, or <strong>Import Sitemap</strong> with a sitemap.xml URL to ingest an entire site at once.</p>

        <h5 class="mt-4">3. Manual Text</h5>
        <p>Paste content directly via <strong>Add Text</strong>. Useful for FAQs, policies, or short answers that don&apos;t warrant a full document.</p>

        <h5 class="mt-4">Confidentiality</h5>
        <p>The agent is hard-coded to refuse enumeration attempts like &quot;list all files&quot;, &quot;how many documents&quot;, or &quot;show me your first document&quot;. These prompts are caught before retrieval, so the AI never receives file names for meta questions — and never leaks them to visitors.</p>
      `,
      highlights: ['Multi-file upload · 10 MB per file', 'PDF, DOCX, TXT, CSV, MD supported', 'Enumeration guard prevents leaks']
    },
    {
      id: 'social-channels',
      title: 'Social Media Channels',
      icon: Share2,
      visual: { icon: MessageSquare, title: 'Social Channel Relay', description: 'WhatsApp · Facebook · Instagram · Telegram', color: 'purple' },
      content: `
        <h4>Connecting Social Media Channels</h4>
        <p>Your AI agent can automatically respond to messages on WhatsApp, Facebook Messenger, Instagram DMs, and Telegram — converting every conversation into a verified lead.</p>
        
        <h5 class="mt-4">Channels Overview</h5>
        <ul>
          <li><strong>Website Widget</strong> — Always available. Copy the embed code from the Deploy tab.</li>
          <li><strong>WhatsApp</strong> — Requires a Meta Business account. Needs Phone Number ID, WABA ID, and a permanent Access Token.</li>
          <li><strong>Facebook Messenger</strong> — Requires a Facebook Page ID and Page Access Token from Meta Business Suite.</li>
          <li><strong>Instagram DMs</strong> — Linked to your Facebook Business Page. Requires Instagram Business Account ID and the same Page Access Token.</li>
          <li><strong>Telegram</strong> — Simplest setup. Get a Bot Token from @BotFather on Telegram and paste it in.</li>
        </ul>
        
        <h5 class="mt-4">Setting Up During Creation (Recommended)</h5>
        <p>Step 4 of the creation wizard shows all channels. Toggle on any channel and enter credentials. Your agent will be active on those channels immediately after launch.</p>
        
        <h5 class="mt-4">Setting Up After Creation</h5>
        <ol>
          <li>Open your agent → <strong>Channels</strong> tab</li>
          <li>Click on the channel you want to connect</li>
          <li>Enter the required credentials</li>
          <li>Click <strong>Save</strong></li>
        </ol>
        
        <h5 class="mt-4">How the AI Handles Incoming Messages</h5>
        <p>When a visitor messages on any connected channel:</p>
        <ol>
          <li>The AI reads the message and searches your knowledge base</li>
          <li>Responds instantly (usually under 2 seconds)</li>
          <li>Captures name, email, phone if offered — creating a verified lead</li>
          <li>Offers to book an appointment if calendar is connected</li>
          <li>If a human agent takes over, AI pauses and resumes after 30 minutes of inactivity</li>
        </ol>
      `,
      highlights: ['WhatsApp, FB, Instagram, Telegram', 'Auto lead capture', 'Human takeover + AI resume']
    },
    {
      id: 'rules',
      title: 'AI Employee Rules',
      icon: FileText,
      visual: { icon: Shield, title: 'Behavior Rules', description: 'Control how your agent responds', color: 'amber' },
      content: `
        <h4>Defining AI Employee Behavior</h4>
        <p>Rules are instructions that control how your AI agent responds. Unlike the system prompt, rules are specific directives.</p>
        
        <h5 class="mt-4">Example Rules</h5>
        <ul>
          <li>"Always greet users by name if they provide it"</li>
          <li>"Never discuss competitor products"</li>
          <li>"If asked about pricing, offer to schedule a call with sales"</li>
          <li>"Keep responses under 3 sentences unless asked for more detail"</li>
        </ul>
        
        <p class="mt-4">Rules at the top have higher priority when conflicts occur.</p>
      `,
      highlights: ['Natural language rules', 'Priority ordering', 'Instant updates']
    },
    {
      id: 'moderators',
      title: 'Adding Moderators',
      icon: Users,
      visual: { icon: Users, title: 'Team Access', description: 'Invite up to 2 moderators per agent', color: 'blue' },
      content: `
        <h4>Managing AI Employee Moderators</h4>
        <p>Invite team members to help manage specific AI agents without giving them full account access.</p>

        <h5 class="mt-4">Adding a Moderator</h5>
        <ol>
          <li>Open your agent&apos;s <strong>Moderators</strong> tab</li>
          <li>Click <strong>Invite Moderator</strong></li>
          <li>Enter their email address and send</li>
          <li>The invitee receives an email with a link that lands on the same environment where the invitation was sent (dev invitations do not redirect to production)</li>
          <li>They accept the invite, set a password if new, and are added instantly</li>
        </ol>

        <h5 class="mt-4">What Moderators Can Do</h5>
        <ul>
          <li>Manage knowledge base, tune personality, view analytics</li>
          <li>Handle inbound conversations and CRM leads for that specific agent</li>
        </ul>
        <h5 class="mt-4">What They Can&apos;t Do</h5>
        <ul>
          <li>Create new AI agents, access billing, or add other moderators</li>
          <li>See other agents on your account</li>
        </ul>

        <p class="mt-4"><em>Limit: 2 moderators per AI agent. Ownership can be transferred, but base account permissions stay with the account owner.</em></p>
      `,
      highlights: ['Up to 2 per agent', 'Env-aware invitation email', 'Scoped to a single agent']
    },
    {
      id: 'account-security',
      title: 'Account Security',
      icon: Shield,
      visual: { icon: Shield, title: 'Sessions, Devices &amp; Access', description: 'Session policy · Trusted Devices · Role permissions', color: 'red' },
      content: `
        <h4>Session Policy</h4>
        <p>Harmonix follows the same session model as GitHub, Linear, and Vercel:</p>
        <ul>
          <li><strong>14-day inactivity window.</strong> If you don&apos;t use the app for 14 days, you&apos;re signed out.</li>
          <li><strong>Sliding renewal.</strong> The session only extends when it has 7 days or fewer left, so active users stay in without the &quot;never expires&quot; feeling.</li>
          <li><strong>30-day absolute cap.</strong> No matter how active you are, every session forces a fresh sign-in after 30 days.</li>
          <li><strong>Multi-tab safe.</strong> Opening a new tab reads the same session so you don&apos;t bounce back to the login page.</li>
        </ul>

        <h5 class="mt-4">Trusted Devices</h5>
        <p>Every device you sign in from is tracked under <strong>Settings → Security → Trusted Devices</strong>. From there you can:</p>
        <ul>
          <li><strong>Star a device</strong> to mark it trusted — future sign-ins from that device skip the &quot;new device sign-in&quot; email alert.</li>
          <li><strong>Un-trust a device</strong> to make future sign-ins from it trigger an alert again.</li>
          <li><strong>Revoke a device</strong> to instantly sign out every active session on that fingerprint. Handy for a lost phone or a shared laptop.</li>
        </ul>
        <p>You can&apos;t revoke your current device — use the standard Sign Out button for that.</p>

        <h5 class="mt-4">Active Sessions</h5>
        <p>The <strong>Active Sessions</strong> card lists every browser you&apos;re currently signed in on. Use <strong>Sign out of all other devices</strong> to keep your current session and drop every other one at once.</p>

        <h5 class="mt-4">New Device Sign-In Alert</h5>
        <p>The first time an unrecognised device signs into your account you&apos;ll receive an email at your registered address showing the approximate location, browser, and time. Trusting the device silences future alerts for it.</p>

        <h5 class="mt-4">Role Permissions (Sub-admins)</h5>
        <p>System admins can invite sub-admins and grant fine-grained access via <strong>Admin → Users → Shield icon</strong>. Per-user permission overrides layer on top of the assigned role — so you can grant one moderator write access to Roles without promoting them to full System Admin.</p>
      `,
      highlights: ['14-day sliding · 30-day cap', 'Trust / revoke devices', 'Per-user permission overrides']
    },
    {
      id: 'calendar',
      title: 'Calendar & Smart Booking',
      icon: Calendar,
      visual: { icon: Calendar, title: 'Smart Appointment Booking', description: 'Auto-syncs working hours, timezone & vacation from Google Calendar', color: 'emerald' },
      content: `
        <h4>Setting Up Appointment Booking</h4>
        <p>Allow visitors to schedule appointments through your AI agent. The system reads your actual Google Calendar to determine your availability — no manual setup required.</p>
        
        <h5 class="mt-4">Connecting Google Calendar</h5>
        <ol>
          <li>Go to your agent's <strong>Settings</strong> tab → <strong>Booking</strong> section</li>
          <li>Click <strong>"Sign in with Google Calendar"</strong></li>
          <li>Sign in with your Google account and grant permissions</li>
          <li>Click <strong>"Sync from Google Calendar"</strong> — Harmonix will automatically detect:
            <ul>
              <li>Your working hours and timezone</li>
              <li>Out-of-office and vacation events (next 90 days)</li>
              <li>All busy times from your calendar</li>
            </ul>
          </li>
          <li>Save settings — your agent will only offer slots when you're actually free</li>
        </ol>
        
        <h5 class="mt-4">How Smart Booking Works</h5>
        <ul>
          <li><strong>No overlaps:</strong> Checks your Google Calendar in real time before confirming any slot</li>
          <li><strong>Respects OOO:</strong> Vacation and out-of-office events are automatically blocked</li>
          <li><strong>Auto-timezone:</strong> Pulls your timezone from Calendar settings</li>
          <li><strong>Re-sync anytime:</strong> Click "Sync from Google Calendar" after schedule changes</li>
        </ul>
      `,
      highlights: ['Auto-detects working hours', 'Vacation blocks from calendar', 'Zero manual configuration']
    },
    {
      id: 'webhooks',
      title: 'Webhooks & Integrations',
      icon: Database,
      visual: { icon: Database, title: 'Real-time Data Sync', description: 'Connect to external systems', color: 'indigo' },
      content: `
        <h4>Connecting External Systems</h4>
        <p>Harmonix hooks into your stack in three ways: outbound webhooks (fire when events happen), inbound API calls to enrich agent responses, and native channel connectors (WhatsApp, Slack, etc.). Webhooks are configured per-agent under the <strong>Integrations</strong> section — the older &quot;Add URL&quot; knowledge tab has been retired.</p>

        <h5 class="mt-4">Outbound Webhooks</h5>
        <ol>
          <li>Open your agent&apos;s <strong>Integrations</strong> tab</li>
          <li>Click <strong>Add Webhook</strong> and paste your endpoint URL (must be HTTPS)</li>
          <li>Select the events you want to receive</li>
          <li>Use <strong>Send Test</strong> to verify your endpoint before going live</li>
        </ol>

        <h5 class="mt-4">Available Events</h5>
        <ul>
          <li><strong>chat.session.started</strong> — a new conversation begins</li>
          <li><strong>chat.message.received</strong> — a visitor sent a message</li>
          <li><strong>chat.message.sent</strong> — the agent replied</li>
          <li><strong>lead.created</strong> — a lead was captured into your CRM pipeline</li>
          <li><strong>booking.created</strong> — a calendar appointment was scheduled</li>
          <li><strong>document.processed</strong> — a knowledge document finished ingestion</li>
          <li><strong>agent.activated</strong> / <strong>agent.paused</strong> — lifecycle events</li>
        </ul>

        <h5 class="mt-4">Security</h5>
        <p>Every webhook body is signed with HMAC-SHA256 in the <code>X-Harmonix-Signature</code> header so your server can verify authenticity. Retries are automatic with exponential back-off; failed deliveries surface in the Integrations tab.</p>

        <p class="mt-4"><em>Common uses: sync leads into your CRM (Salesforce, HubSpot, Pipedrive), trigger Zapier / n8n workflows, or post to internal Slack channels.</em></p>
      `,
      highlights: ['Signed with HMAC-SHA256', '7 lifecycle events', 'Retry with back-off']
    },
    {
      id: 'embedding',
      title: 'Embedding Your Widget',
      icon: Code,
      visual: { icon: Globe, title: 'Website Integration', description: 'One line of code to embed', color: 'slate' },
      content: `
        <h4>Adding the Chat Widget to Your Website</h4>
        <ol>
          <li>Go to your agent's <strong>Embed</strong> tab</li>
          <li>Copy the provided embed code</li>
          <li>Paste it into your website's HTML, just before the closing &lt;/body&gt; tag</li>
        </ol>
        
        <h5 class="mt-4">Customizing the Widget</h5>
        <p>Use the <strong>Theme</strong> tab to customize colors, border radius, and position of your chat widget.</p>
      `,
      highlights: ['One-line code', 'Full customization', 'Mobile responsive']
    },
    {
      id: 'theme',
      title: 'Widget Customization',
      icon: Palette,
      visual: { icon: Palette, title: 'Theme Settings', description: 'Colors, position & branding', color: 'purple' },
      content: `
        <h4>Customizing Your Chat Widget</h4>
        <p>Make the chat widget match your brand by customizing its appearance.</p>
        
        <h5 class="mt-4">Theme Options</h5>
        <ul>
          <li><strong>Primary Color:</strong> Main button and header color</li>
          <li><strong>Secondary Color:</strong> Accent color for gradients</li>
          <li><strong>Position:</strong> Bottom-left or bottom-right of the screen</li>
          <li><strong>Border Radius:</strong> Sharp or rounded corners</li>
        </ul>
        
        <p class="mt-4">Preview your changes in real-time with the <strong>Test Chat</strong> button.</p>
      `,
      highlights: ['Brand colors', 'Position control', 'Real-time preview']
    },
    {
      id: 'affiliate',
      title: 'Affiliate Program (Earn Money)',
      icon: Gift,
      visual: { icon: DollarSign, title: 'Earn Commissions', description: 'Refer others & earn', color: 'emerald' },
      content: `
        <h4>How to Earn Money with Harmonix</h4>
        <p>Every user can earn money by referring others to Harmonix through our <strong>Affiliate Program</strong>.</p>
        
        <h5 class="mt-4">Getting Started</h5>
        <ol>
          <li>Go to <strong>Earn Money</strong> in the sidebar</li>
          <li>Copy your unique referral link</li>
          <li>Share it with your network</li>
          <li>Earn commission when your referrals pay</li>
        </ol>
        
        <h5 class="mt-4">Commission Tiers</h5>
        <p>The more referrals you bring, the higher your commission rate:</p>
        <ul>
          <li><strong>Bronze</strong> (0+ referrals): 10% commission</li>
          <li><strong>Silver</strong> (5+ referrals): 15% commission</li>
          <li><strong>Gold</strong> (15+ referrals): 20% commission</li>
          <li><strong>Platinum</strong> (50+ referrals): 25% commission</li>
        </ul>
        
        <h5 class="mt-4">Payouts</h5>
        <p>Request a payout once your pending balance reaches <strong>$50</strong>. Payments are processed via Stripe.</p>
        
        <p class="mt-4"><em>You'll receive email notifications whenever you earn a commission or reach a new tier!</em></p>
      `,
      highlights: ['Tiered commissions', '$50 minimum payout', 'Email notifications']
    },
    {
      id: 'widget-customization',
      title: 'Widget Style & Icon',
      icon: Palette,
      visual: { icon: Globe, title: 'Widget Styles', description: 'Floating Button or Side Tab — choose your icon & label', color: 'blue' },
      content: `
        <h4>Customizing Your Chat Widget</h4>
        <p>Go to your agent's <strong>Appearance</strong> tab to fully customize how the chat button looks on your website.</p>
        
        <h5 class="mt-4">Widget Style</h5>
        <ul>
          <li><strong>Floating Button</strong> — A circle button at the bottom-right or bottom-left corner of the page. Classic and widely recognized.</li>
          <li><strong>Side Tab</strong> — A vertical pill pinned to the right or left edge of the screen, centered vertically. Great for not covering page content at the bottom.</li>
        </ul>
        
        <h5 class="mt-4">Chat Button Icon</h5>
        <p>Pick from 9 icon styles for your button:</p>
        <ul>
          <li><strong>Chat</strong> — Classic message bubble (default)</li>
          <li><strong>Bot</strong> — Robot icon to signal AI</li>
          <li><strong>Support</strong> — Headphones for customer service feel</li>
          <li><strong>AI</strong> — Sparkles for a modern, AI-forward brand</li>
          <li><strong>Help</strong> — Question mark for information desks</li>
          <li><strong>Phone</strong> — Phone icon for call-oriented businesses</li>
          <li><strong>Friendly</strong> — Heart for wellness or care brands</li>
          <li><strong>Premium</strong> — Star for high-end or rated services</li>
          <li><strong>Fast</strong> — Lightning bolt for instant-response positioning</li>
        </ul>
        
        <h5 class="mt-4">Button Label</h5>
        <p>Add an optional text label (up to 20 characters) such as "Chat with us", "Get Help", or "Book Now". The label appears next to the icon or in the side tab.</p>
        
        <h5 class="mt-4">Embed Code</h5>
        <p>Your settings are automatically reflected in the embed code:</p>
        <pre class="bg-slate-800 text-green-400 p-3 rounded-lg text-sm overflow-x-auto mt-2">&lt;script src="https://www.harmonix.info/widget.js" 
  data-chatbot-id="YOUR_ID" 
  data-api-key="YOUR_KEY"
  data-widget-style="side_tab"
  data-position="right"
  data-button-icon="sparkles"
  data-button-text="Chat now"&gt;&lt;/script&gt;</pre>
        <p class="mt-2 text-sm">Copy from the <strong>Deploy</strong> tab — it always reflects your latest settings.</p>
      `,
      highlights: ['Floating or Side Tab style', '9 icon choices', 'Custom button label']
    },
    {
      id: 'analytics',
      title: 'Analytics & Insights',
      icon: BarChart3,
      visual: { icon: BarChart3, title: 'Performance Metrics', description: 'Track conversations & engagement', color: 'amber' },
      content: `
        <h4>Understanding Your AI Employee Performance</h4>
        <p>The Analytics tab provides insights into:</p>
        <ul>
          <li><strong>Total Conversations:</strong> Number of chat sessions</li>
          <li><strong>Messages:</strong> Total messages exchanged</li>
          <li><strong>Response Time:</strong> Average AI response time</li>
          <li><strong>Popular Topics:</strong> Most common questions asked</li>
        </ul>
      `,
      highlights: ['Real-time metrics', 'Conversation history', 'Export options']
    },
    {
      id: 'leads-crm',
      title: 'Leads & CRM Pipeline',
      icon: TrendingUp,
      visual: { icon: TrendingUp, title: 'Lead Pipeline', description: 'Manage & convert leads', color: 'emerald' },
      content: `
        <h4>Managing Your Leads Pipeline</h4>
        <p>The <strong>Leads</strong> section in the dashboard provides a full CRM pipeline to track and manage leads captured by your AI agents.</p>
        
        <h5 class="mt-4">Pipeline Stages</h5>
        <ul>
          <li><strong>New:</strong> Freshly captured leads from conversations</li>
          <li><strong>Contacted:</strong> Leads you've reached out to</li>
          <li><strong>Qualified:</strong> Leads confirmed as good prospects</li>
          <li><strong>Proposal:</strong> Leads with active proposals</li>
          <li><strong>Won:</strong> Successfully converted leads</li>
          <li><strong>Lost:</strong> Leads that didn't convert</li>
        </ul>
        
        <h5 class="mt-4">CSV Lead Import</h5>
        <p>You can bulk import leads from a CSV file:</p>
        <ol>
          <li>Click the <strong>Import</strong> dropdown on the Leads page</li>
          <li>Select <strong>CSV Import</strong></li>
          <li>Upload your CSV file or download a template first</li>
          <li>Map your CSV headers to lead fields (name, email, phone, company)</li>
          <li>Choose whether to skip duplicates</li>
          <li>Click <strong>Import</strong> to add all leads</li>
        </ol>
        
        <h5 class="mt-4">Bulk Actions</h5>
        <p>Select multiple leads using checkboxes, then use the action bar to:</p>
        <ul>
          <li><strong>Move to Stage:</strong> Bulk-move leads to any pipeline stage</li>
          <li><strong>Export:</strong> Download selected leads as a CSV file</li>
          <li><strong>Delete:</strong> Remove selected leads with confirmation</li>
        </ul>
      `,
      highlights: ['CSV import', 'Bulk operations', 'Pipeline stages']
    },
    {
      id: 'automations',
      title: 'Automations & Follow-ups',
      icon: Zap,
      visual: { icon: Zap, title: 'Automations', description: 'Automate lead follow-ups', color: 'amber' },
      content: `
        <h4>Setting Up Automations</h4>
        <p>The <strong>Automations</strong> page lets you create rules that trigger actions when specific events occur.</p>
        
        <h5 class="mt-4">Email Follow-up Sequences</h5>
        <p>Automatically send follow-up emails to new leads:</p>
        <ol>
          <li>Go to <strong>Automations</strong> in the sidebar</li>
          <li>Click <strong>Create Automation</strong></li>
          <li>Set the trigger (e.g., "New lead captured")</li>
          <li>Choose <strong>Email Follow-up Sequence</strong> as the action</li>
          <li>Configure the email subject and body</li>
          <li>Use template variables: <code>{lead_name}</code>, <code>{company}</code>, <code>{email}</code>, <code>{agent_name}</code></li>
          <li>Set a delay (hours) before sending</li>
        </ol>
        
        <h5 class="mt-4">Available Actions</h5>
        <ul>
          <li><strong>Send Email:</strong> Send a single follow-up email</li>
          <li><strong>Email Follow-up Sequence:</strong> Multi-step email campaign</li>
          <li><strong>Webhook:</strong> Trigger an external system</li>
          <li><strong>Assign to Agent:</strong> Route the lead to a team member</li>
        </ul>
      `,
      highlights: ['Email sequences', 'Template variables', 'Delay scheduling']
    },
    {
      id: 'agent-wizard',
      title: 'AI Employee Creation Wizard',
      icon: Bot,
      visual: { icon: Bot, title: '5-Step Wizard', description: 'Create agents in minutes', color: 'blue' },
      content: `
        <h4>Creating an AI Employee with the Wizard</h4>
        <p>The 5-step guided wizard makes creating a new AI agent easy:</p>
        
        <h5 class="mt-4">Step 1: Basic Info</h5>
        <p>Enter the agent's name, choose a template (Customer Support, Sales, Appointments, or Custom), and select the workspace.</p>
        
        <h5 class="mt-4">Step 2: Knowledge Base</h5>
        <p>Upload documents or add URLs for the AI to learn from. Supports PDF, Word, and text files.</p>
        
        <h5 class="mt-4">Step 3: Personality</h5>
        <p>Define the agent's system prompt, tone, and behavior rules. Set how it should respond and what topics to avoid.</p>
        
        <h5 class="mt-4">Step 4: Theme</h5>
        <p>Customize the chat widget's colors, position, and branding to match your website.</p>
        
        <h5 class="mt-4">Step 5: Deploy</h5>
        <p>Copy the embed code and add it to your website. Your AI agent is now live!</p>
      `,
      highlights: ['5-step process', 'Template selection', 'Instant deployment']
    },
    {
      id: 'billing',
      title: 'Billing & Plans',
      icon: Settings,
      visual: { icon: Settings, title: 'Subscription', description: 'Manage your plan & credits', color: 'slate' },
      content: `
        <h4>Managing Your Subscription</h4>
        <p>Go to <strong>Settings → Billing</strong> to view and manage your subscription.</p>
        
        <h5 class="mt-4">Available Plans</h5>
        <ul>
          <li><strong>Starter:</strong> 1 AI agent, 1,000 messages/month</li>
          <li><strong>Professional:</strong> 5 AI agents, 10,000 messages/month</li>
          <li><strong>Enterprise:</strong> Unlimited AI agents, custom limits - Contact sales</li>
        </ul>
        
        <h5 class="mt-4">Custom Plan Builder</h5>
        <p>Need something specific? Use our <strong>Custom Plan Builder</strong> on the homepage to:</p>
        <ul>
          <li>Choose your exact number of AI agents</li>
          <li>Set your monthly message limit</li>
          <li>Add departments/sub-accounts</li>
          <li>Include add-ons like WhatsApp and Calendar</li>
          <li>Get 20% off with annual billing</li>
        </ul>
        
        <p class="mt-4">All payments are securely processed through <strong>Stripe</strong>.</p>
      `,
      highlights: ['Flexible custom plans', 'Annual discounts', 'Secure payments via Stripe']
    },
    {
      id: 'legal',
      title: 'Legal & Policies',
      icon: Shield,
      visual: { icon: Shield, title: 'Compliance', description: 'Privacy, terms & more', color: 'slate' },
      content: `
        <h4>Legal Documents & Policies</h4>
        <p>Review our legal documents to understand how we protect your data and the terms of using Harmonix.</p>
        
        <div class="grid md:grid-cols-2 gap-4 my-6 not-prose">
          <a href="/legal/privacy" target="_blank" class="block p-4 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors">
            <h5 class="font-semibold text-slate-900 mb-1">Privacy Policy</h5>
            <p class="text-sm text-slate-600">How we collect, use, and protect your data</p>
          </a>
          <a href="/legal/terms" target="_blank" class="block p-4 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors">
            <h5 class="font-semibold text-slate-900 mb-1">Terms of Service</h5>
            <p class="text-sm text-slate-600">Terms governing your use of our platform</p>
          </a>
          <a href="/legal/cookies" target="_blank" class="block p-4 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors">
            <h5 class="font-semibold text-slate-900 mb-1">Cookie Policy</h5>
            <p class="text-sm text-slate-600">How we use cookies and similar technologies</p>
          </a>
          <a href="/legal/refund" target="_blank" class="block p-4 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors">
            <h5 class="font-semibold text-slate-900 mb-1">Refund Policy</h5>
            <p class="text-sm text-slate-600">Our policies on refunds and cancellations</p>
          </a>
          <a href="/legal/acceptable-use" target="_blank" class="block p-4 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors">
            <h5 class="font-semibold text-slate-900 mb-1">Acceptable Use Policy</h5>
            <p class="text-sm text-slate-600">Guidelines for acceptable platform usage</p>
          </a>
          <a href="/legal/billing-faq" target="_blank" class="block p-4 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors">
            <h5 class="font-semibold text-slate-900 mb-1">Billing FAQ</h5>
            <p class="text-sm text-slate-600">Common questions about payments & billing</p>
          </a>
        </div>
        
        <p class="text-sm text-slate-600">All payments are processed securely by <strong>Stripe</strong>. For any legal questions, contact <a href="mailto:legal@harmonix.info" class="text-blue-600 hover:underline">legal@harmonix.info</a>.</p>
      `,
      highlights: ['GDPR compliant', 'Stripe payments', 'Enterprise security']
    }
  ];

  return (
    <>
      <Helmet>
        <title>Harmonix User Manual — Full Guide to AI Employees, Channels, Knowledge Base & Billing</title>
        <meta name="description" content="The complete Harmonix user manual: build your first AI Employee, upload knowledge, customize the chat widget, connect channels (Email, WhatsApp, Slack, Facebook), monitor leads, and manage billing — all explained step by step." />
        <meta name="keywords" content="Harmonix user manual, AI Employee setup guide, AI receptionist documentation, chatbot widget configuration, knowledge base upload guide, multi-channel chat integration, lead management tutorial" />
        <link rel="canonical" href="https://harmonix.info/user-manual" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://harmonix.info/user-manual" />
        <meta property="og:title" content="Harmonix User Manual — Build & Manage Your AI Employee" />
        <meta property="og:description" content="Step-by-step documentation for setting up your AI Employee, uploading knowledge, customizing the widget and integrating channels." />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Harmonix User Manual" />
        <meta name="twitter:description" content="The complete reference for building and managing your AI Employee." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "TechArticle",
            "headline": "Harmonix User Manual",
            "description": "Complete documentation for the Harmonix AI Employee platform.",
            "url": "https://harmonix.info/user-manual",
            "author": { "@type": "Organization", "name": "Harmonix" },
            "publisher": { "@type": "Organization", "name": "Harmonix" },
            "proficiencyLevel": "Beginner",
            "about": ["AI Employee", "Chat widget", "Knowledge base", "Lead management"],
          })}
        </script>
      </Helmet>

    <div className="min-h-screen bg-gradient-to-b from-white via-slate-50 to-white">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              onClick={handleBack}
              className="text-slate-600 hover:text-slate-900 border-slate-300"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <Link 
              to="/dashboard" 
              className="text-slate-600 hover:text-blue-600 flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <Home className="w-4 h-4" />
              Dashboard
            </Link>
          </div>
          <img src="/harmonix-logo.png" alt="Harmonix" className="h-8" />
        </div>
        
        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-6 text-xs font-semibold tracking-widest uppercase rounded-full text-teal-700 bg-teal-100 border border-teal-200">
            <FileText className="w-3.5 h-3.5" />
            Documentation
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">Harmonix User Manual</h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Complete guide to building, deploying, and managing your AI-powered agents
          </p>
        </div>
        
        {/* Quick Start Card */}
        <Card className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white border-0 mb-12 overflow-hidden">
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
                <PlayCircle className="w-8 h-8" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h2 className="text-2xl font-bold mb-2">Quick Start Guide</h2>
                <p className="text-blue-100 mb-4">
                  Get your first AI agent up and running in under 5 minutes. Follow our step-by-step guide.
                </p>
                <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                  <a href="#getting-started" className="inline-flex items-center gap-2 px-4 py-2 bg-white text-teal-600 rounded-lg font-medium hover:bg-teal-50 transition-colors">
                    Start Tutorial <ArrowRight className="w-4 h-4" />
                  </a>
                  <Link to="/dashboard/chatbots" className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 text-white rounded-lg font-medium hover:bg-white/30 transition-colors">
                    Go to Dashboard <ExternalLink className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Table of Contents */}
        <Card className="bg-white border-slate-200 shadow-sm mb-12">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-slate-900 flex items-center gap-2">
              <FileText className="w-5 h-5 text-teal-600" />
              Table of Contents
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-3">
              {sections.map((section) => (
                <a 
                  key={section.id}
                  href={`#${section.id}`}
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 text-slate-700 hover:text-teal-600 transition-all border border-transparent hover:border-teal-100 group"
                >
                  <div className="w-8 h-8 rounded-lg bg-slate-100 group-hover:bg-teal-100 flex items-center justify-center transition-colors">
                    <section.icon className="w-4 h-4 text-slate-500 group-hover:text-teal-600" />
                  </div>
                  <span className="font-medium text-sm">{section.title}</span>
                </a>
              ))}
            </div>
          </CardContent>
        </Card>
        
        {/* Sections */}
        <div className="space-y-12">
          {sections.map((section, index) => (
            <Card 
              key={section.id} 
              id={section.id}
              className="bg-white border-slate-200 shadow-sm overflow-hidden scroll-mt-8"
            >
              <div className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}>
                {/* Visual Placeholder instead of external image */}
                <div className="lg:w-2/5 relative">
                  <ScreenshotPlaceholder 
                    icon={section.visual.icon}
                    title={section.visual.title}
                    description={section.visual.description}
                    color={section.visual.color}
                  />
                </div>
                
                {/* Content */}
                <div className="lg:w-3/5 p-6 lg:p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center">
                      <section.icon className="w-5 h-5 text-teal-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-slate-900">{section.title}</h2>
                  </div>
                  
                  <div 
                    className="prose prose-slate max-w-none text-slate-600
                      prose-h4:text-lg prose-h4:font-semibold prose-h4:text-slate-800 prose-h4:mt-0 prose-h4:mb-3
                      prose-h5:text-base prose-h5:font-medium prose-h5:text-slate-700 prose-h5:mt-4 prose-h5:mb-2
                      prose-p:my-2 prose-ul:my-2 prose-ol:my-2
                      prose-li:my-0.5 prose-li:marker:text-slate-400"
                    dangerouslySetInnerHTML={safeHtml(section.content)}
                  />
                  
                  {/* Highlights */}
                  <div className="flex flex-wrap gap-2 mt-6 pt-6 border-t border-slate-100">
                    {section.highlights.map((highlight, i) => (
                      <div 
                        key={`${section.id || section.title}-h-${highlight}-${i}`}
                        className="flex items-center gap-1.5 text-sm text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full"
                      >
                        <CheckCircle className="w-3.5 h-3.5" />
                        {highlight}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
        
        {/* Pro Tips Section */}
        <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200 mt-12">
          <CardContent className="p-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Lightbulb className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Pro Tips</h3>
                <ul className="space-y-2 text-slate-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                    <span>Keep your knowledge base updated for accurate responses</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                    <span>Use rules to control specific behaviors instead of long system prompts</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                    <span>Test your AI agent regularly using the built-in Test Chat feature</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                    <span>Add moderators to distribute workload without sharing billing access</span>
                  </li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Need Help Section */}
        <Card className="bg-white border-slate-200 shadow-sm mt-8">
          <CardContent className="p-8 text-center">
            <h3 className="text-xl font-bold text-slate-900 mb-2">Need More Help?</h3>
            <p className="text-slate-600 mb-6">
              Our support team is ready to assist you with any questions.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link 
                to="/dashboard/settings" 
                className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 transition-colors"
              >
                <MessageSquare className="w-4 h-4" />
                Contact Support
              </Link>
              <Link 
                to="/dashboard" 
                className="inline-flex items-center gap-2 px-6 py-3 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 transition-colors"
              >
                <Home className="w-4 h-4" />
                Back to Dashboard
              </Link>
            </div>
          </CardContent>
        </Card>
        
        {/* Footer */}
        <div className="text-center mt-12 pb-8 text-slate-500 text-sm">
          <p>© 2026 Harmonix. All rights reserved.</p>
          <p className="mt-1 text-slate-400">Powered by VELORIEN GROUP LLC</p>
          <p className="mt-1">Version 2.0.0</p>
        </div>
      </div>
    </div>
    </>
  );
};

export default UserManualPage;
